import React from 'react'
import { Box, Typography, Collapse } from '@material-ui/core'
import { Icon } from '@iconify/react'
import { CoreButton } from 'src/components/core'
import AddIcon from '@material-ui/icons/Add'
import { withStyles } from '@material-ui/core/styles'
import GoalItem from './GoalItem'
import styles from '../styles'
import { useSelector } from 'react-redux'

const ModuleContent = ({
  classes,
  module,
  sectionIndex,
  moduleIndex,
  expandedModules,
  goalRefs,
  popupStates,
  anchorElements,
  fieldSuggestions,
  popupLoadingStates,
  onAddGoal,
  onAddGoalWithText,
  onRemoveGoal,
  onRemoveLastGoal,
  onGoalChange,
  onGoalFocus,
  onNewGoalFocus,
  onRegenerateSuggestions,
  onPopupClose,
  onSuggestionClick,
  onAddPage,
  getFileInfo,
  onGoalKeyDown,
  documentFollowing,
  setDocumentFollowing,
}) => {
  const moduleKey = `${sectionIndex}-${moduleIndex}`
  const isExpanded = expandedModules[moduleKey]
  const goals = module.outline || []
  const fileInfo = getFileInfo(module)
  const attachments = useSelector(state => state.aICreator.attachments)
  const handleGoalKeyDown = (e, sectionIndex, moduleIndex, goalIndex) => {
    // Don't handle goal keydown for survey, quiz, or feedback modules
    if (
      module.content_type === 'survey' ||
      module.content_type === 'quiz' ||
      module.content_type === 'feedback'
    ) {
      return
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      // Check if this is the last goal
      if (goalIndex === goals.length - 1) {
        onAddGoal(sectionIndex, moduleIndex)
      }
    }
    // For backspace handling, we'll use the onKeyDown prop that comes from the hook
  }

  // Check if this module should show goals (hide for quiz, feedback, and survey content types)
  const isQuizOrFeedback =
    module.content_type === 'quiz' ||
    module.content_type === 'feedback' ||
    module.content_type === 'survey'

  // For quiz, feedback, and survey modules, don't show anything (no accordion, no goals)
  if (isQuizOrFeedback) {
    return null
  }

  const getSourceDetails = () => {
    if (!fileInfo && !module.attachment) return null

    // Find matched attachment from redux if available
    const matchedAtt = module.attachment ? attachments.find(
      a => a.attachment_id === module.attachment.id,
    ) : null
    
    const isYoutube = matchedAtt && matchedAtt.attachment_type === 'youtube'
    const isWeb = matchedAtt && matchedAtt.attachment_type === 'web'

    let title = (fileInfo && fileInfo.name) || ''
    if (matchedAtt) {
      if (!title || isYoutube || isWeb) {
        title =
          matchedAtt.file_url ||
          matchedAtt.source_url ||
          matchedAtt.original_file_name ||
          matchedAtt.file_name ||
          title
      }
    }

    const start_seconds = module.attachment ? module.attachment.start_seconds : null
    const end_seconds = module.attachment ? module.attachment.end_seconds : null
    
    const formatTime = (seconds) => {
      if (seconds == null) return null
      const m = Math.floor(seconds / 60)
      const s = Math.floor(seconds % 60)
      return `${m}:${s.toString().padStart(2, '0')}`
    }
    const startTimeFormatted = formatTime(start_seconds)
    const endTimeFormatted = formatTime(end_seconds)
    
    let timestampStr = ''
    if (startTimeFormatted && endTimeFormatted) {
      timestampStr = ` (${startTimeFormatted} - ${endTimeFormatted})`
    } else if (startTimeFormatted) {
      timestampStr = ` (${startTimeFormatted})`
    }

    let url = (module.attachment && module.attachment.url) || (matchedAtt && matchedAtt.source_url) || ''
    if (isYoutube && url && start_seconds != null) {
      try {
        const urlObj = new URL(url)
        urlObj.searchParams.set('t', Math.floor(start_seconds))
        url = urlObj.toString()
      } catch (e) {
        if (url.includes('?')) url += `&t=${Math.floor(start_seconds)}`
        else url += `?t=${Math.floor(start_seconds)}`
      }
    }

    return {
      isYoutube,
      isWeb,
      title,
      chapter: fileInfo ? fileInfo.chapter : (module.element_id || 'No chapter specified'),
      url,
      timestampStr
    }
  }

  const sourceDetails = getSourceDetails()

  // For other modules, use the accordion behavior
  const goalsWithAttachmentCheck =
    (attachments.length === 0 && documentFollowing === 'reference') ||
    (attachments.length > 0 && documentFollowing !== 'reference')

  const renderSourceInfo = () => {
    if (!sourceDetails) return null
    return (
      <Box className={classes.fileInfo}>
        <Icon
          icon={
            sourceDetails.isYoutube
              ? 'ph:video-camera'
              : sourceDetails.isWeb
              ? 'ph:globe'
              : 'proicons:file'
          }
          className={classes.fileIcon}
        />
        <Typography className={classes.fileText}>
          {sourceDetails.isYoutube || sourceDetails.isWeb ? (
            <>
              Source:{' '}
              {sourceDetails.url ? (
                <a
                  href={sourceDetails.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'inherit', textDecoration: 'underline' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {sourceDetails.title}
                </a>
              ) : (
                sourceDetails.title
              )}
              {sourceDetails.timestampStr}
            </>
          ) : (
            `File: ${sourceDetails.title} Chapter: ${sourceDetails.chapter}`
          )}
        </Typography>
      </Box>
    )
  }

  return (
    <>
      {!goalsWithAttachmentCheck ? (
        <Collapse in={isExpanded}>
          <Box className={classes.pageContent}>
            <Typography className={classes.goalsLabel}>Page goals</Typography>

            <Box className={classes.goalsList}>
              {goals.map((goal, goalIndex) => (
                <GoalItem
                  key={goalIndex}
                  classes={classes}
                  goal={goal}
                  sectionIndex={sectionIndex}
                  moduleIndex={moduleIndex}
                  goalIndex={goalIndex}
                  goalRefs={goalRefs}
                  onGoalChange={onGoalChange}
                  onRemoveGoal={onRemoveGoal}
                  onGoalFocus={onGoalFocus}
                  onKeyDown={onGoalKeyDown}
                  popupStates={popupStates}
                  anchorElements={anchorElements}
                  fieldSuggestions={fieldSuggestions}
                  popupLoadingStates={popupLoadingStates}
                  onRegenerateSuggestions={onRegenerateSuggestions}
                  onPopupClose={onPopupClose}
                  onSuggestionClick={onSuggestionClick}
                />
              ))}
            </Box>

            {renderSourceInfo()}
          </Box>
        </Collapse>
      ) : (
        sourceDetails && (
          // For other modules with attachment check, show file info without accordion
          <Box className={classes.pageContent}>
            {renderSourceInfo()}
          </Box>
        )
      )}
    </>
  )
}

export default withStyles(styles)(ModuleContent)
